import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { recebimentosService, Recebimento, Frequencia } from '../services/recebimentos';
import { asaasService } from '../services/asaas';
import { categoriasService, Categoria } from '../services/categorias';
import { entidadesService, Entidade } from '../services/entidades';
import { DateRangePicker, DateRange } from '../components/ui/DateRangePicker';
import { DatePicker } from '../components/ui/DatePicker';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { formatCurrency, formatDateBR } from '../utils/format';

const startOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
const endOfMonth = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth()+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
};

export const Recebimentos: React.FC = () => {
  const [items, setItems] = useState<Recebimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [entidades, setEntidades] = useState<Entidade[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Mês inteiro por default (inclui recebimentos futuros do mês)
  const [dateRange, setDateRange] = useState<DateRange>({
    start: startOfMonth(),
    end: endOfMonth()
  });
  const [filters, setFilters] = useState({
    categoria_id: '',
    origem: '',
    status: ''
  });

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data: new Date().toISOString().substring(0, 10),
    entidade_id: '',
    categoria_id: '',
    status: 'pendente' as 'pendente' | 'pago' | 'vencido',
    is_recorrente: false,
    frequencia: 'mensal' as Frequencia,
  });
  const [actionFeedback, setActionFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [quickReceiveDialog, setQuickReceiveDialog] = useState<Recebimento | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const showActionFeedback = (message: string, type: 'success' | 'error' = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => setActionFeedback(null), 2500);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let data = await recebimentosService.list({
        data_inicio: dateRange.start,
        data_fim: dateRange.end,
        // Permite ver cancelados quando o user explicitamente filtrar por esse status
        incluir_cancelado: filters.status === 'cancelado',
      });

      if (filters.categoria_id) {
        data = data.filter((d: Recebimento) => d.categoria_id === Number(filters.categoria_id));
      }
      if (filters.origem) {
        data = data.filter((d: Recebimento) => d.origem === filters.origem);
      }
      if (filters.status) {
        data = data.filter((d: Recebimento) => d.status === filters.status);
      }

      setItems(data);
    } catch (error) {
      console.error('Erro ao buscar recebimentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      const [cats, ents] = await Promise.all([
        categoriasService.list('receita'),
        entidadesService.list('cliente')
      ]);
      setCategorias(cats);
      setEntidades(ents);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  useEffect(() => {
    loadInitialData();
    // Auto-sync ao carregar a página
    const autoSync = async () => {
      try {
        await asaasService.syncPayments();
        fetchData();
      } catch (err) {
        console.error('Erro no auto-sync:', err);
      }
    };
    autoSync();
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dateRange]);

  const openEditModal = (item: Recebimento) => {
    setEditingId(item.id);
    setFormData({
      descricao: item.descricao,
      valor: item.valor.toString().replace('.', ','),
      data: item.data_recebimento,
      entidade_id: item.entidade_id?.toString() || '',
      categoria_id: item.categoria_id?.toString() || '',
      status: item.status as any,
      is_recorrente: item.is_recorrente || false,
      frequencia: (item.frequencia as Frequencia) || 'mensal',
    });
    setIsModalOpen(true);
  };

  const handleCreateOrUpdate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const valorNum = Number(formData.valor.replace(',', '.'));
    if (!formData.descricao.trim()) {
      showActionFeedback('Informe a descrição', 'error');
      return;
    }
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      showActionFeedback('Valor deve ser maior que zero', 'error');
      return;
    }

    try {
      const payload: any = {
        descricao: formData.descricao,
        valor: valorNum,
        data_recebimento: formData.data,
        status: formData.status,
        origem: editingId ? undefined : 'manual',
        entidade_id: formData.entidade_id ? Number(formData.entidade_id) : null,
        categoria_id: formData.categoria_id ? Number(formData.categoria_id) : null,
        is_recorrente: formData.is_recorrente,
      };
      if (formData.is_recorrente) {
        payload.frequencia = formData.frequencia;
      }

      if (editingId) {
        await recebimentosService.update(editingId, payload as Partial<Recebimento>);
      } else {
        await recebimentosService.create(payload);
      }

      closeModal();
      fetchData();
      showActionFeedback(editingId ? 'Recebimento atualizado' : 'Recebimento criado');
    } catch (error: any) {
      showActionFeedback(error?.response?.data?.error || 'Erro ao salvar lançamento', 'error');
    }
  };

  const handleDelete = () => {
    if (!editingId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!editingId) return;
    try {
      await recebimentosService.delete(editingId);
      setDeleteDialogOpen(false);
      closeModal();
      fetchData();
      showActionFeedback('Recebimento excluído');
    } catch (error) {
      showActionFeedback('Erro ao excluir lançamento', 'error');
    }
  };

  /** Atalho rápido: abrir confirmação para marcar como recebido. */
  const openQuickReceiveDialog = (rec: Recebimento, e: React.MouseEvent) => {
    e.stopPropagation();
    if (rec.status === 'pago') return;
    setQuickReceiveDialog(rec);
  };

  const confirmQuickReceive = async (dataRecebimento?: string) => {
    if (!quickReceiveDialog || !dataRecebimento) return;
    try {
      await recebimentosService.marcarRecebido(quickReceiveDialog.id, dataRecebimento);
      setQuickReceiveDialog(null);
      fetchData();
      showActionFeedback('Recebimento confirmado');
    } catch (error: any) {
      showActionFeedback(error?.response?.data?.error || 'Erro ao confirmar recebimento', 'error');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      descricao: '',
      valor: '',
      data: new Date().toISOString().substring(0, 10),
      entidade_id: '',
      categoria_id: '',
      status: 'pendente',
      is_recorrente: false,
      frequencia: 'mensal',
    });
  };

  return (
    <Layout pageTitle="Recebimentos" breadcrumb="Financeiro">
      <ConfirmDialog
        isOpen={!!quickReceiveDialog}
        title="Marcar recebimento"
        highlight={quickReceiveDialog?.descricao}
        description="Confirma o recebimento? O status será atualizado para 'Pago' e a data informada será gravada como data de recebimento."
        icon="paid"
        confirmLabel="Confirmar recebimento"
        input={{
          label: 'Data de recebimento',
          type: 'date',
          defaultValue: new Date().toISOString().substring(0, 10),
          required: true,
        }}
        onCancel={() => setQuickReceiveDialog(null)}
        onConfirm={confirmQuickReceive}
      />

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Excluir recebimento?"
        highlight={items.find(i => i.id === editingId)?.descricao}
        description="Esta ação não pode ser desfeita. Caso queira manter o histórico, considere marcar como cancelado."
        icon="delete"
        variant="danger"
        confirmLabel="Excluir"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />

      {actionFeedback && (
        <div className="fixed top-6 right-6 z-[9999] animate-fade-in-right">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border backdrop-blur-xl ${
            actionFeedback.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}>
            <span className="material-symbols-outlined">
              {actionFeedback.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="font-bold text-sm">{actionFeedback.message}</span>
          </div>
        </div>
      )}
      <div className="space-y-8 animate-fade-in-up">
        {/* Título e Botões de Ação */}
        <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white">Recebimentos</h2>
            <p className="text-slate-500 mt-1">Entradas via Asaas e lançamentos manuais.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  await asaasService.syncPayments();
                  fetchData();
                  alert('Sincronização concluída!');
                } catch (err) {
                  alert('Erro ao sincronizar');
                } finally {
                  setLoading(false);
                }
              }}
              className="flex items-center gap-2 text-xs font-bold text-slate-400 border border-slate-700/50 rounded-xl px-4 py-2 bg-slate-800/40 hover:bg-slate-700/80 hover:text-white transition-all duration-300"
            >
              <span className="material-symbols-outlined text-[16px] text-primary">sync</span> Sincronizar Asaas
            </button>
            <button className="flex items-center gap-2 text-xs font-bold text-slate-400 border border-slate-700/50 rounded-xl px-4 py-2 bg-slate-800/40 hover:bg-slate-700/80 hover:text-white transition-all duration-300">
              <span className="material-symbols-outlined text-[16px]">download</span> Exportar PDF
            </button>
            <button
              onClick={() => {
                setEditingId(null);
                setIsModalOpen(true);
              }}
              className="bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Novo Lançamento
            </button>
          </div>
        </section>

        {/* Filtros em Cards Glass */}
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range)}
          />

          <div className="relative group">
            <select
              value={filters.categoria_id}
              onChange={(e) => setFilters({ ...filters, categoria_id: e.target.value })}
              className="appearance-none bg-slate-800/40 border border-slate-700/50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition-all cursor-pointer hover:border-slate-600 w-52 text-slate-300"
            >
              <option value="">Todas as categorias</option>
              {categorias.map(cat => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:scale-110 group-hover:text-slate-300 transition-transform">expand_more</span>
          </div>

          <div className="relative group">
            <select
              value={filters.origem}
              onChange={(e) => setFilters({ ...filters, origem: e.target.value })}
              className="appearance-none bg-slate-800/40 border border-slate-700/50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition-all cursor-pointer hover:border-slate-600 w-44 text-slate-300"
            >
              <option value="">Todas as origens</option>
              <option value="asaas">Asaas</option>
              <option value="manual">Manual</option>
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:scale-110 group-hover:text-slate-300 transition-transform">expand_more</span>
          </div>

          <div className="relative group">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="appearance-none bg-slate-800/40 border border-slate-700/50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition-all cursor-pointer hover:border-slate-600 w-44 text-slate-300"
            >
              <option value="">Todos os status</option>
              <option value="pago">Pago</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:scale-110 group-hover:text-slate-300 transition-transform">expand_more</span>
          </div>
        </div>

        {/* Tabela Interativa Premium */}
        <section className="glass-panel rounded-2xl overflow-hidden border-slate-800/50 hover:border-slate-700/50 transition-all duration-500">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead>
                <tr className="border-b border-primary/10 bg-primary/5">
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-24">Data</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Descrição / Cliente</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Categoria</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center hidden xl:table-cell">Origem</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Valor</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center w-28">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/5">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-10 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Carregando...</p>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-slate-500 text-sm italic">Nenhum lançamento encontrado para este período.</td>
                  </tr>
                ) : items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => openEditModal(item)}
                    className="hover:bg-slate-800/30 transition-all duration-300 group cursor-pointer"
                  >
                    <td className="p-5 text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                      {formatDateBR(item.data_recebimento)}
                    </td>
                    <td className="p-4">
                      <p className="text-sm font-black text-slate-200 group-hover:text-white transition-all leading-tight line-clamp-1">
                        {item.descricao || 'Sem descrição'}
                      </p>
                      <p className="text-[10px] text-slate-500 group-hover:text-slate-400 transition-colors mt-0.5 flex items-center gap-1.5">
                        {item.entidade_nome ? (
                          <>
                            <span className="material-symbols-outlined text-[12px]">person</span>
                            {item.entidade_nome}
                          </>
                        ) : (
                          <span className="italic">Sem cliente vinculado</span>
                        )}
                      </p>
                    </td>
                    <td className="p-4 text-sm text-slate-400 hidden lg:table-cell">
                      <span className="bg-slate-800/50 border border-slate-700/50 px-2 py-1 rounded-md text-[10px]">{item.categoria_nome || '—'}</span>
                    </td>
                    <td className="p-4 text-center hidden xl:table-cell">
                      <span className={`inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider transition-colors ${item.origem === 'asaas'
                        ? 'bg-slate-800/80 border border-slate-700/50 text-slate-300 group-hover:bg-slate-700/80'
                        : 'bg-slate-800/40 border border-slate-700/30 text-slate-400 group-hover:bg-slate-700/50'
                        }`}>
                        {item.origem === 'asaas' ? 'Asaas' : 'Manual'}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-black text-right text-emerald-400 transition-colors">
                      + {formatCurrency(item.valor)}
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center">
                        <StatusBadge status={item.status} />
                      </div>
                    </td>
                    <td className="p-4 text-right w-20" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {item.status !== 'pago' && item.status !== 'cancelado' && (
                          <button
                            onClick={(e) => openQuickReceiveDialog(item, e)}
                            title="Marcar como recebido"
                            className="p-1.5 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                          >
                            <span className="material-symbols-outlined text-[18px]">paid</span>
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(item)}
                          title="Editar"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-primary hover:bg-primary/10 transition-all"
                        >
                          <span className="material-symbols-outlined text-[18px]">edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-800/80 bg-slate-800/30">
                  <td colSpan={5} className="p-5 text-xs font-bold text-slate-300 uppercase tracking-wider">Total do Período</td>
                  <td className="p-5 text-lg font-black text-right text-emerald-400">+ {formatCurrency(items.reduce((sum, r) => sum + Number(r.valor), 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </div>

      {/* Modal - Novo Lançamento */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background-dark/80 backdrop-blur-sm animate-fade-in-up transition-opacity"
            onClick={() => setIsModalOpen(false)}
          />

          <form
            onSubmit={(e) => { e.preventDefault(); handleCreateOrUpdate(); }}
            className="relative w-full max-w-lg glass-panel bg-background-dark/95 border border-slate-700/50 rounded-2xl shadow-2xl p-8 animate-fade-in-up"
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined text-[24px]">close</span>
            </button>

            <h3 className="text-2xl font-black mb-1 text-white">{editingId ? 'Editar Recebimento' : 'Novo Recebimento'}</h3>
            <p className="text-sm text-slate-500 mb-8">{editingId ? 'Atualize os dados do lançamento manual.' : 'Lance uma nova entrada financeira no fluxo.'}</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Descrição *</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={255}
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Consultoria XYZ"
                  className="w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all placeholder:text-slate-600 text-white invalid:border-rose-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Valor (R$) *</label>
                  <input
                    type="text"
                    required
                    inputMode="decimal"
                    pattern="^\d+([.,]\d{1,2})?$"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    placeholder="0,00"
                    title="Use apenas números (ex: 1500 ou 1500,50)"
                    className="w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-lg font-bold focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all placeholder:text-slate-600 text-white invalid:border-rose-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Vencimento</label>
                  <DatePicker
                    value={formData.data}
                    onChange={(val) => setFormData({ ...formData, data: val })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Cliente</label>
                  <select
                    value={formData.entidade_id}
                    onChange={(e) => setFormData({ ...formData, entidade_id: e.target.value })}
                    className="appearance-none w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all text-slate-300 cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    {entidades.map(ent => <option key={ent.id} value={ent.id}>{ent.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Categoria</label>
                  <select
                    value={formData.categoria_id}
                    onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    className="appearance-none w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all text-slate-300 cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    {categorias.map((cat: Categoria) => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                <div className="flex gap-4">
                  <label className="flex-1 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      className="peer sr-only"
                      checked={formData.status === 'pendente'}
                      onChange={() => setFormData({ ...formData, status: 'pendente' })}
                    />
                    <div className="text-center py-2.5 px-4 rounded-xl border border-slate-700/50 bg-slate-800/40 peer-checked:bg-slate-700/80 peer-checked:border-slate-500 peer-checked:text-white transition-all font-bold text-sm text-slate-400 group-hover:border-slate-600">
                      Pendente
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      className="peer sr-only"
                      checked={formData.status === 'pago'}
                      onChange={() => setFormData({ ...formData, status: 'pago' })}
                    />
                    <div className="text-center py-2.5 px-4 rounded-xl border border-slate-700/50 bg-slate-800/40 peer-checked:bg-slate-700/80 peer-checked:border-slate-500 peer-checked:text-white transition-all font-bold text-sm text-slate-400 group-hover:border-slate-600">
                      Pago
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-2">
                <label className="flex items-center gap-3 cursor-pointer group w-fit">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={formData.is_recorrente}
                      onChange={(e) => setFormData({ ...formData, is_recorrente: e.target.checked })}
                    />
                    <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:bg-primary transition-all"></div>
                    <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full peer-checked:translate-x-5 transition-all"></div>
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider group-hover:text-slate-300 transition-colors">É recorrente?</span>
                </label>

                {formData.is_recorrente && (
                  <div className="mt-4 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30 animate-fade-in-up">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Frequência</label>
                    <div className="flex bg-slate-900/50 p-1 rounded-lg border border-slate-700/50">
                      {(['mensal', 'quinzenal', 'anual'] as Frequencia[]).map((f) => (
                        <button
                          type="button"
                          key={f}
                          onClick={() => setFormData({ ...formData, frequencia: f })}
                          className={`flex-1 py-1.5 text-[11px] font-bold uppercase rounded-md transition-all ${formData.frequencia === f ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                      ⓘ Próximas ocorrências geradas automaticamente pelo cron <code>recurring:generate</code> 7 dias antes do recebimento.
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-4 mt-6 border-t border-slate-800/50 flex justify-between gap-3">
                <div>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold text-rose-500 hover:bg-rose-500/10 transition-all flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                      Excluir
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700/50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2.5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 hover:-translate-y-0.5 shadow-lg shadow-primary/20 transition-all"
                  >
                    {editingId ? 'Salvar Alterações' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};
